<script lang="ts">
  import * as leaflet from 'leaflet';
  import 'leaflet/dist/leaflet.css';
  import type {
    CircleMarkerOptions,
    LatLngExpression,
    Layer,
    Map as LeafletMap,
    PolylineOptions,
  } from 'leaflet';
  import { onMount } from 'svelte';
  import { lineColor } from '../lib/mode-colors';
  import type { BoardingHintJson } from '../types/BoardingHintJson';
  import type { JourneyJson } from '../types/JourneyJson';
  import type { LegJson } from '../types/LegJson';
  import type { StopJson } from '../types/StopJson';

  /** Default map centre (Berlin). */
  const BERLIN_CENTER: LatLngExpression = [52.52, 13.41];
  const DEFAULT_ZOOM = 12;
  const WALKING_COLOR = '#8b949e';
  /** Colour of the train-axis polyline drawn between the leading and trailing car ends —
   *  desaturated indigo, high contrast on OSM's pale tiles without funeral-black vibes. */
  const BOARDING_TRAIN_COLOR = '#4c51bf';
  /** Colour of the amber highlight around the door closest to `from_front`. */
  const BOARDING_TARGET_COLOR = '#f6c343';
  /** Fill colour used by both regular door circles and the target door — kept high
   *  contrast against the train line so every dot is legible. */
  const BOARDING_DOOR_FILL = '#ffffff';
  /** Minimum on-ground axis length (metres) the overlay is rendered at. Short OSM
   *  features (some Berlin platforms report ~20 m) are extended symmetrically from
   *  the midpoint to this length so the line and doors remain visible on the map. */
  const BOARDING_MIN_LENGTH_M = 80;
  /** Metres per degree at the equator; used for the frontend length-in-metres math
   *  and for extending the axis when it falls under `BOARDING_MIN_LENGTH_M`. */
  const METRES_PER_DEG = 111_320;
  /** Colour of station-entrance dots — bright green so they stand out from the amber
   *  target door and the near-black train line. */
  const BOARDING_ENTRANCE_COLOR = '#3fb950';
  /** Radius of a station-entrance dot. Sized to remain visible at fit-to-journey
   *  zoom (~z=14) where an 80 m platform axis renders as ~14 px on screen. */
  const BOARDING_ENTRANCE_RADIUS = 8;
  /** Fixed door count per train visualization — five evenly-spaced markers. */
  const BOARDING_DOOR_COUNT = 5;
  /** Zoom level at which the boarding overlay becomes visible; below this the train
   *  spans a single pixel and the amber highlight cannot be distinguished. */
  const BOARDING_MIN_ZOOM = 14;
  /** Opacity applied to overlay layers when the current zoom passes `BOARDING_MIN_ZOOM`. */
  const BOARDING_VISIBLE_OPACITY = 0.9;
  /** Opacity applied to overlay layers when the current zoom is below `BOARDING_MIN_ZOOM`. */
  const BOARDING_HIDDEN_OPACITY = 0;

  interface Properties {
    /** Called once the Leaflet map is initialised, so callers can draw layers. */
    onReady?: (mapInstance: LeafletMap) => void;
    /**
     * Called on unmount right before the Leaflet map is destroyed so callers can
     * null out any cached reference they took in `onReady`.
     */
    onDestroy?: () => void;
    /** Journey to render as polylines + stop markers. `undefined` clears the map. */
    journey?: JourneyJson;
    /** Initial map centre. Defaults to Berlin. Only read at mount. */
    center?: LatLngExpression;
    /** Initial zoom level. Only read at mount. */
    zoom?: number;
  }

  const {
    onReady,
    onDestroy: onDestroyProperty,
    journey,
    center = BERLIN_CENTER,
    zoom = DEFAULT_ZOOM,
  }: Properties = $props();

  /** Cached Leaflet layers for one leg — one polyline plus its stop markers. */
  interface LayerCacheEntry {
    polyline: Layer;
    markers: Layer[];
    /** Boarding-hint overlay layers (train polyline + 5 door circles). Empty
     *  array for walking legs and hint-free transit legs so the pruner can
     *  iterate uniformly without a null check. */
    boardingLayers: Layer[];
  }

  const domState = $state<{ mapElement: HTMLDivElement | undefined }>({ mapElement: undefined });
  const mapState: { mapReference: LeafletMap | undefined } = { mapReference: undefined };
  /**
   * Per-leg layer cache keyed by object identity — WeakMap keeps entries alive
   * only while their leg is still reachable via the current journey response,
   * so an obsolete journey's layers get GC'd once we drop them from `activeLegs`.
   */
  const layerCache = new WeakMap<LegJson, LayerCacheEntry>();
  /** Legs currently mounted on the map; used to prune layers when the journey changes (WeakMap can't be iterated). */
  const mountedState: { activeLegs: LegJson[] } = { activeLegs: [] };

  onMount(() => {
    /* istanbul ignore if -- defensive: `bind:this` binds mapEl before onMount fires */
    if (!domState.mapElement) return;
    const mapInstance = leaflet.map(domState.mapElement).setView(center, zoom);
    leaflet
      .tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        maxZoom: 19,
      })
      .addTo(mapInstance);
    mapState.mapReference = mapInstance;
    mapInstance.on('zoomend', () => {
      applyBoardingVisibility(mapInstance);
    });
    onReady?.(mapInstance);
    return () => {
      onDestroyProperty?.();
      mapInstance.remove();
      mapState.mapReference = undefined;
      mountedState.activeLegs = [];
    };
  });

  /** Iterates every mounted leg's cached overlay and syncs its opacity to the map's
   *  current zoom via `setStyle`; layers stay attached across the toggle. */
  function applyBoardingVisibility(mapInstance: LeafletMap): void {
    const opacity = overlayOpacity(mapInstance.getZoom());
    for (const activeLeg of mountedState.activeLegs) applyLegOverlayOpacity(activeLeg, opacity);
  }

  /** Overlay opacity for a given map zoom — visible above threshold, hidden below. */
  function overlayOpacity(zoomLevel: number): number {
    return zoomLevel >= BOARDING_MIN_ZOOM ? BOARDING_VISIBLE_OPACITY : BOARDING_HIDDEN_OPACITY;
  }

  /** Applies `opacity` to every overlay layer of one leg (no-op when the leg has none). */
  function applyLegOverlayOpacity(activeLeg: LegJson, opacity: number): void {
    const entry = layerCache.get(activeLeg);
    /* istanbul ignore if -- defensive: activeLegs mirrors legs painted into the cache */
    if (!entry) return;
    for (const overlayLayer of entry.boardingLayers) setLayerOpacity(overlayLayer, opacity);
  }

  /** Applies `opacity` and `fillOpacity` to a Leaflet path layer via `setStyle`. */
  function setLayerOpacity(layer: Layer, opacity: number): void {
    (
      layer as unknown as { setStyle: (options: { opacity: number; fillOpacity: number }) => void }
    ).setStyle({ opacity, fillOpacity: opacity });
  }

  function walkingCoords(walkingLeg: Extract<LegJson, { type: 'walking' }>): [number, number][] {
    if (walkingLeg.geometry.length > 1) return walkingLeg.geometry;
    return [
      [walkingLeg.from.lat, walkingLeg.from.lon],
      [walkingLeg.to.lat, walkingLeg.to.lon],
    ];
  }

  function transitCoords(stops: StopJson[]): [number, number][] {
    return stops.map((stop) => [stop.lat, stop.lon]);
  }

  function makePolyline(
    mapInstance: LeafletMap,
    coords: [number, number][],
    options: PolylineOptions,
  ): Layer {
    return leaflet.polyline(coords, options).addTo(mapInstance);
  }

  function makeCircleMarker(
    mapInstance: LeafletMap,
    stop: StopJson,
    options: CircleMarkerOptions,
  ): Layer {
    return leaflet
      .circleMarker([stop.lat, stop.lon], options)
      .bindTooltip(stop.name)
      .addTo(mapInstance);
  }

  /** Bare circle marker without a bound tooltip — used for boarding-hint doors and
   *  station entrances where a per-dot tooltip would just clutter the overlay. */
  function makeBareCircleMarker(
    mapInstance: LeafletMap,
    coord: [number, number],
    options: CircleMarkerOptions,
  ): Layer {
    return leaflet.circleMarker(coord, options).addTo(mapInstance);
  }

  function walkingStopStyle(): CircleMarkerOptions {
    return {
      radius: WALK_STOP_RADIUS,
      color: WALKING_COLOR,
      fillColor: '#0d1117',
      fillOpacity: 1,
      weight: 2,
    };
  }

  function cacheWalkingLegLayers(
    mapInstance: LeafletMap,
    walkingLeg: Extract<LegJson, { type: 'walking' }>,
    coords: [number, number][],
  ): void {
    if (layerCache.has(walkingLeg)) return;
    const polyline = makePolyline(mapInstance, coords, {
      color: WALKING_COLOR,
      weight: 4,
      dashArray: '6 4',
      opacity: 0.9,
    });
    const style = walkingStopStyle();
    const markers = [walkingLeg.from, walkingLeg.to].map((stop) =>
      makeCircleMarker(mapInstance, stop, style),
    );
    layerCache.set(walkingLeg, { polyline, markers, boardingLayers: [] });
  }

  function paintWalkingLeg(
    mapInstance: LeafletMap,
    walkingLeg: Extract<LegJson, { type: 'walking' }>,
  ): [number, number][] {
    const coords = walkingCoords(walkingLeg);
    cacheWalkingLegLayers(mapInstance, walkingLeg, coords);
    return coords;
  }

  function transitStopStyle(
    color: string,
    isEndpoint: boolean,
  ): CircleMarkerOptions & { radius: number } {
    const radius = isEndpoint ? TRANSIT_ENDPOINT_RADIUS : TRANSIT_STOP_RADIUS;
    return {
      radius,
      color,
      fillColor: isEndpoint ? color : '#0d1117',
      fillOpacity: 1,
      weight: 2,
    };
  }

  function transitMarkers(
    mapInstance: LeafletMap,
    transitLeg: Extract<LegJson, { type: 'transit' }>,
    color: string,
  ): Layer[] {
    const lastIndex = transitLeg.stops.length - 1;
    return transitLeg.stops.map((stop, index) => {
      const style = transitStopStyle(color, index === 0 || index === lastIndex);
      return makeCircleMarker(mapInstance, stop, style);
    });
  }

  /** Endpoints of the visible train axis. Extends short OSM axes symmetrically
   *  from the midpoint to `BOARDING_MIN_LENGTH_M` so the overlay remains legible
   *  at typical map zooms. Coincident `axis_front === axis_rear` (currentLength
   *  zero) collapses to the midpoint pair rather than dividing by zero and
   *  producing `NaN` coords that would corrupt Leaflet's fitBounds. */
  function visibleAxis(hint: BoardingHintJson): [[number, number], [number, number]] {
    const [frontLat, frontLon] = hint.axis_front;
    const [rearLat, rearLon] = hint.axis_rear;
    const midLat = (frontLat + rearLat) / 2;
    const midLon = (frontLon + rearLon) / 2;
    const dlat = (rearLat - frontLat) * METRES_PER_DEG;
    const cosLat = Math.cos((midLat * Math.PI) / 180);
    const dlon = (rearLon - frontLon) * METRES_PER_DEG * cosLat;
    const currentLength = Math.hypot(dlat, dlon);
    if (currentLength === 0) {
      return [
        [midLat, midLon],
        [midLat, midLon],
      ];
    }
    const scale = currentLength < BOARDING_MIN_LENGTH_M ? BOARDING_MIN_LENGTH_M / currentLength : 1;
    const halfLat = ((rearLat - frontLat) / 2) * scale;
    const halfLon = ((rearLon - frontLon) / 2) * scale;
    return [
      [midLat - halfLat, midLon - halfLon],
      [midLat + halfLat, midLon + halfLon],
    ];
  }

  /** Precomputed segment lengths + total for `polyline`. Built once per boarding
   *  overlay by `polylineMetrics` and shared across every `coordAtFractionCached`
   *  call so the door loop is O(V) instead of O(D·V) (five doors each re-summing V
   *  segments). Kept ownership at the caller boundary — no module-scope memoisation. */
  interface PolylineMetrics {
    polyline: [number, number][];
    segLengths: number[];
    total: number;
  }

  /** Bundles a polyline with its cos(lat)-corrected metric segment lengths and their
   *  sum. Caller owns the returned struct; single-vertex polylines produce an empty
   *  `segLengths` array and `total = 0`. */
  function polylineMetrics(polyline: [number, number][]): PolylineMetrics {
    const segLengths = segmentLengths(polyline);
    const total = segLengths.reduce((accumulator, next) => accumulator + next, 0);
    return { polyline, segLengths, total };
  }

  /** Point at `fraction` (0..1) along the metrics' polyline, measured by cumulative
   *  segment length. Empty/single-vertex polylines collapse to the first vertex;
   *  out-of-range fractions clamp to the endpoints. Reuses the pre-summed lengths so
   *  a five-door render never re-sums the polyline. */
  function coordAtFractionCached(metrics: PolylineMetrics, fraction: number): [number, number] {
    const clamped = clampFractionToPolyline(metrics.polyline, fraction);
    if (clamped !== undefined) return clamped;
    return coordAtCumulative(metrics.polyline, metrics.segLengths, metrics.total * fraction);
  }

  /** Endpoint of `polyline` to short-circuit to when `fraction` is out of range;
   *  `undefined` when the caller must sum segments. Callers guarantee `polyline.length >= 2`
   *  (via [`boardingPolylineCoords`], which returns either a clipped rail polyline of at
   *  least two vertices or a length-2 axis pair). */
  function clampFractionToPolyline(
    polyline: [number, number][],
    fraction: number,
  ): [number, number] | undefined {
    if (fraction <= 0) return polyline[0];
    if (fraction >= 1) return polyline.at(-1);
    return undefined;
  }

  /** Finds the segment containing `target` metres from the start, then interpolates
   *  within it. Clamps to the final segment when `target` exceeds the polyline length
   *  (float-drift safety). Caller guarantees `segLengths.length >= 1`. */
  function coordAtCumulative(
    polyline: [number, number][],
    segLengths: number[],
    target: number,
  ): [number, number] {
    const { index, remaining } = locateSegment(segLengths, target);
    return interpolateAlongSegment(polyline, index, remaining, segLengths[index]);
  }

  /** Returns the index of the segment whose cumulative length first meets or exceeds
   *  `target`, plus the distance into that segment. Clamps to the final segment when
   *  `target` overshoots the total (protects against `total * 1.0` drifting past
   *  `sum` by an ULP). */
  function locateSegment(
    segLengths: number[],
    target: number,
  ): { index: number; remaining: number } {
    let travelled = 0;
    let hitIndex = segLengths.length - 1;
    for (const [index, segLength] of segLengths.entries()) {
      if (travelled + segLength >= target) {
        hitIndex = index;
        break;
      }
      travelled += segLength;
    }
    const remaining = Math.max(0, Math.min(segLengths[hitIndex], target - travelled));
    return { index: hitIndex, remaining };
  }

  /** Linearly interpolates `distanceIntoSegment` metres along segment `index`. */
  function interpolateAlongSegment(
    polyline: [number, number][],
    index: number,
    distanceIntoSegment: number,
    segLength: number,
  ): [number, number] {
    const withinSegment = segLength === 0 ? 0 : distanceIntoSegment / segLength;
    const [aLat, aLon] = polyline[index];
    const [bLat, bLon] = polyline[index + 1];
    return [aLat + (bLat - aLat) * withinSegment, aLon + (bLon - aLon) * withinSegment];
  }

  /** Metric distance (metres) of each consecutive-vertex segment. */
  function segmentLengths(polyline: [number, number][]): number[] {
    const lengths: number[] = [];
    for (let index = 0; index < polyline.length - 1; index++) {
      const [aLat, aLon] = polyline[index];
      const [bLat, bLon] = polyline[index + 1];
      const midLat = (aLat + bLat) / 2;
      const deltaLat = (bLat - aLat) * METRES_PER_DEG;
      const deltaLon = (bLon - aLon) * METRES_PER_DEG * Math.cos((midLat * Math.PI) / 180);
      lengths.push(Math.hypot(deltaLat, deltaLon));
    }
    return lengths;
  }

  /** Index of the door closest to the rider-relative `from_front` position (0.0 front, 1.0 rear). */
  function targetDoorIndex(hint: BoardingHintJson): number {
    return Math.round(hint.from_front * (BOARDING_DOOR_COUNT - 1));
  }

  /** Picks the target-highlighted style when `isTarget`, else the plain door style. */
  function doorStyle(isTarget: boolean): CircleMarkerOptions {
    return isTarget ? TARGET_DOOR_STYLE : REGULAR_DOOR_STYLE;
  }

  /** Coordinates of the rail-following polyline the frontend draws: the backend's
   *  `rail_polyline` clipped to a train-length window around the anchor when
   *  non-empty, or the min-length-extended synth axis pair otherwise. */
  function boardingPolylineCoords(hint: BoardingHintJson): [number, number][] {
    if (hint.rail_polyline.length >= 2) {
      const anchor = axisAnchor(hint);
      return clipPolylineAroundAnchor(hint.rail_polyline, anchor, BOARDING_MIN_LENGTH_M);
    }
    const axis = visibleAxis(hint);
    return [axis[0], axis[1]];
  }

  /** Midpoint of the axis endpoints — used as the anchor when clipping the rail
   *  polyline down to a train-length window. */
  function axisAnchor(hint: BoardingHintJson): [number, number] {
    const [frontLat, frontLon] = hint.axis_front;
    const [rearLat, rearLon] = hint.axis_rear;
    return [(frontLat + rearLat) / 2, (frontLon + rearLon) / 2];
  }

  /** Returns the sub-polyline covering `lengthM` metres, centered on the point of
   *  `polyline` closest to `anchor`. Walks outward from the projected foot in both
   *  directions along cumulative segment length, always taking at least one hop on
   *  each side ([`walkOutward`] always includes the first neighbour so downstream
   *  door-placement math has at least one segment). Assembles
   *  `slice(back, insertAt) + foot + slice(insertAt, forward)` in one allocation —
   *  no intermediate `stitched` copy. */
  function clipPolylineAroundAnchor(
    polyline: [number, number][],
    anchor: [number, number],
    lengthM: number,
  ): [number, number][] {
    const projected = projectAnchorOntoPolyline(polyline, anchor);
    const insertAt = projected.insertAt;
    const foot = projected.foot;
    const half = lengthM / 2;
    const backIndex = walkOutward(polyline, insertAt - 1, foot, half, -1);
    const forwardIndex = walkOutward(polyline, insertAt, foot, half, 1);
    return [
      ...polyline.slice(backIndex, insertAt),
      foot,
      ...polyline.slice(insertAt, forwardIndex + 1),
    ];
  }

  /** Projects `anchor` onto the polyline and returns the perpendicular-foot coord
   *  along with the vertex index to insert it at (so the anchor sits between
   *  vertices `insertAt-1` and `insertAt`). Falls back to the first vertex when
   *  the polyline is degenerate. */
  function projectAnchorOntoPolyline(
    polyline: [number, number][],
    anchor: [number, number],
  ): { foot: [number, number]; insertAt: number } {
    let best = { foot: polyline[0], insertAt: 1, distance: Infinity };
    for (let index = 0; index < polyline.length - 1; index++) {
      const foot = perpendicularFoot(anchor, polyline[index], polyline[index + 1]);
      const distance = footDistanceM(anchor, foot);
      if (distance < best.distance) {
        best = { foot, insertAt: index + 1, distance };
      }
    }
    return { foot: best.foot, insertAt: best.insertAt };
  }

  /** Foot of the perpendicular from `point` onto segment `segStart→segEnd`, clamped to segment ends. */
  function perpendicularFoot(
    point: [number, number],
    segStart: [number, number],
    segEnd: [number, number],
  ): [number, number] {
    const [pointLat, pointLon] = point;
    const [aLat, aLon] = segStart;
    const [bLat, bLon] = segEnd;
    const midLat = (aLat + bLat) / 2;
    const scaleLon = METRES_PER_DEG * Math.cos((midLat * Math.PI) / 180);
    const aXOffset = (aLon - pointLon) * scaleLon;
    const aYOffset = (aLat - pointLat) * METRES_PER_DEG;
    const bXOffset = (bLon - pointLon) * scaleLon;
    const bYOffset = (bLat - pointLat) * METRES_PER_DEG;
    const deltaX = bXOffset - aXOffset;
    const deltaY = bYOffset - aYOffset;
    const segLengthSq = deltaX * deltaX + deltaY * deltaY;
    if (segLengthSq === 0) return segStart;
    const clamped = Math.max(
      0,
      Math.min(1, (-aXOffset * deltaX + -aYOffset * deltaY) / segLengthSq),
    );
    return [aLat + (bLat - aLat) * clamped, aLon + (bLon - aLon) * clamped];
  }

  /** Metric distance between anchor and its projected foot on the polyline. */
  function footDistanceM(anchor: [number, number], foot: [number, number]): number {
    const [anchorLat, anchorLon] = anchor;
    const [footLat, footLon] = foot;
    const cosLat = Math.cos((((anchorLat + footLat) / 2) * Math.PI) / 180);
    const deltaX = (footLon - anchorLon) * METRES_PER_DEG * cosLat;
    const deltaY = (footLat - anchorLat) * METRES_PER_DEG;
    return Math.hypot(deltaX, deltaY);
  }

  /** Signed direction for [`walkOutward`]: `-1` walks toward index 0, `+1` walks toward
   *  the last vertex. Named literals so the caller doesn't spell a magic number. */
  type WalkDirection = -1 | 1;

  /** Walks `polyline` outward from just before `startIndex` in `direction`, paying the
   *  segment cost from `pivot` (foot of anchor) to the first visited vertex and then
   *  vertex-to-vertex, until `budget` metres are consumed OR the walk steps off the
   *  polyline. Returns the outermost visited vertex. The first vertex on each side is
   *  ALWAYS included — even when its segment alone exceeds `budget` — so the clipped
   *  polyline always has at least one vertex on each side of `pivot` when the polyline
   *  has one available (needed by downstream door-placement math). Subsequent hops
   *  break when the cumulative distance reaches `budget`. */
  function walkOutward(
    polyline: [number, number][],
    startIndex: number,
    pivot: [number, number],
    budget: number,
    direction: WalkDirection,
  ): number {
    let cursor = startIndex - direction;
    let previousCoord = pivot;
    let remaining = budget;
    while (remaining > 0) {
      const step = advanceOutward(polyline, cursor, previousCoord, remaining, direction);
      if (step === undefined) break;
      cursor = step.nextCursor;
      previousCoord = step.currentCoord;
      remaining = step.remainingAfter;
    }
    return cursor;
  }

  /** Payload of a single [`walkOutward`] step: the coord just reached, the metres left
   *  in the budget after paying that segment (may go negative on the overshoot hop),
   *  and the cursor now positioned at that coord. `undefined` when the next hop would
   *  step off the polyline. */
  interface WalkStep {
    currentCoord: [number, number];
    remainingAfter: number;
    nextCursor: number;
  }

  /** Attempts one hop of [`walkOutward`]: computes the next cursor, checks bounds,
   *  pays the segment cost. Returns the resulting step or `undefined` to stop. Kept
   *  as a single-branch helper so `walkOutward` stays under the workspace's
   *  cognitive-complexity cap. */
  function advanceOutward(
    polyline: [number, number][],
    cursor: number,
    previousCoord: [number, number],
    remaining: number,
    direction: WalkDirection,
  ): WalkStep | undefined {
    const nextCursor = cursor + direction;
    if (nextCursor < 0 || nextCursor >= polyline.length) return undefined;
    const currentCoord = polyline[nextCursor];
    const segLength = segmentLengthM(
      previousCoord[0],
      previousCoord[1],
      currentCoord[0],
      currentCoord[1],
    );
    return { currentCoord, remainingAfter: remaining - segLength, nextCursor };
  }

  /** Metric length of a single lat/lon segment via cos(lat)-corrected planar math. */
  function segmentLengthM(aLat: number, aLon: number, bLat: number, bLon: number): number {
    const midLat = (aLat + bLat) / 2;
    const deltaX = (bLon - aLon) * METRES_PER_DEG * Math.cos((midLat * Math.PI) / 180);
    const deltaY = (bLat - aLat) * METRES_PER_DEG;
    return Math.hypot(deltaX, deltaY);
  }

  /** Circle-marker style for a station-entrance dot — small, bright green, high contrast. */
  function entranceStyle(): CircleMarkerOptions {
    return {
      radius: BOARDING_ENTRANCE_RADIUS,
      color: BOARDING_ENTRANCE_COLOR,
      fillColor: BOARDING_ENTRANCE_COLOR,
      fillOpacity: 1,
      weight: 2,
    };
  }

  /** Build the rail-shaped polyline, five door markers, and any station-entrance dots.
   *  `polylineMetrics` is computed once and shared across every door so five doors
   *  cost one polyline scan, not five. */
  function makeBoardingOverlay(mapInstance: LeafletMap, hint: BoardingHintJson): Layer[] {
    const metrics = polylineMetrics(boardingPolylineCoords(hint));
    const trainLine = makePolyline(mapInstance, metrics.polyline, {
      color: BOARDING_TRAIN_COLOR,
      weight: 8,
      opacity: 0.9,
    });
    const target = targetDoorIndex(hint);
    const doors = Array.from({ length: BOARDING_DOOR_COUNT }, (_, index) => {
      const fraction = index / (BOARDING_DOOR_COUNT - 1);
      return makeBareCircleMarker(
        mapInstance,
        coordAtFractionCached(metrics, fraction),
        doorStyle(index === target),
      );
    });
    const entrances = hint.entrances.map((coord) =>
      makeBareCircleMarker(mapInstance, coord, entranceStyle()),
    );
    return [trainLine, ...doors, ...entrances];
  }

  function cacheTransitLegLayers(
    mapInstance: LeafletMap,
    transitLeg: Extract<LegJson, { type: 'transit' }>,
    coords: [number, number][],
  ): void {
    if (layerCache.has(transitLeg)) return;
    const color = lineColor(transitLeg);
    const polyline = makePolyline(mapInstance, coords, { color, weight: 5, opacity: 1 });
    const markers = transitMarkers(mapInstance, transitLeg, color);
    const boardingLayers: Layer[] = transitLeg.boarding_hint
      ? makeBoardingOverlay(mapInstance, transitLeg.boarding_hint)
      : [];
    layerCache.set(transitLeg, { polyline, markers, boardingLayers });
    if (boardingLayers.length > 0) applyBoardingVisibility(mapInstance);
  }

  function paintTransitLeg(
    mapInstance: LeafletMap,
    transitLeg: Extract<LegJson, { type: 'transit' }>,
  ): [number, number][] {
    const coords = transitCoords(transitLeg.stops);
    cacheTransitLegLayers(mapInstance, transitLeg, coords);
    return coords;
  }

  const WALK_STOP_RADIUS = 4;
  const TRANSIT_STOP_RADIUS = 3;
  const TRANSIT_ENDPOINT_RADIUS = 5;
  /** Radius of a regular boarding-hint door circle. Bumped so the door cluster
   *  reads as a row of dots at fit-to-journey zoom, not a single blob. */
  const BOARDING_DOOR_RADIUS = 6;
  /** Radius of the highlighted target door — larger than a regular door for legibility. */
  const BOARDING_TARGET_RADIUS = 10;
  /** Circle-marker style for the amber-highlighted target door. */
  const TARGET_DOOR_STYLE: CircleMarkerOptions = {
    radius: BOARDING_TARGET_RADIUS,
    color: BOARDING_TARGET_COLOR,
    fillColor: BOARDING_TARGET_COLOR,
    fillOpacity: 1,
    weight: 2,
  };
  /** Circle-marker style for a regular non-target door. */
  const REGULAR_DOOR_STYLE: CircleMarkerOptions = {
    radius: BOARDING_DOOR_RADIUS,
    color: BOARDING_TRAIN_COLOR,
    fillColor: BOARDING_DOOR_FILL,
    fillOpacity: 1,
    weight: 2,
  };
  const BOUNDS_PAD = 0.15;

  function paintLeg(mapInstance: LeafletMap, journeyLeg: LegJson): [number, number][] {
    if (journeyLeg.type === 'walking') return paintWalkingLeg(mapInstance, journeyLeg);
    return paintTransitLeg(mapInstance, journeyLeg);
  }

  function paintJourney(mapInstance: LeafletMap, activeJourney: JourneyJson): [number, number][] {
    const combined: [number, number][] = [];
    for (const journeyLeg of activeJourney.legs) {
      const coords = paintLeg(mapInstance, journeyLeg);
      for (const coord of coords) combined.push(coord);
    }
    return combined;
  }

  function removeLegLayers(mapInstance: LeafletMap, journeyLeg: LegJson): void {
    const entry = layerCache.get(journeyLeg);
    /* istanbul ignore if -- defensive: pruneRemovedLegs only visits legs that have been painted */
    if (!entry) return;
    mapInstance.removeLayer(entry.polyline);
    for (const marker of entry.markers) mapInstance.removeLayer(marker);
    for (const overlayLayer of entry.boardingLayers) mapInstance.removeLayer(overlayLayer);
    layerCache.delete(journeyLeg);
  }

  function pruneRemovedLegs(mapInstance: LeafletMap, currentLegs: readonly LegJson[]): void {
    const currentSet = new Set(currentLegs);
    for (const oldLeg of mountedState.activeLegs) {
      if (currentSet.has(oldLeg)) continue;
      removeLegLayers(mapInstance, oldLeg);
    }
  }

  $effect(() => {
    /* istanbul ignore if -- defensive: the effect re-runs when journey changes; mapRef is set in onMount which runs first */
    if (!mapState.mapReference) return;
    const nextLegs = journey?.legs ?? [];
    pruneRemovedLegs(mapState.mapReference, nextLegs);
    mountedState.activeLegs = [...nextLegs];
    if (nextLegs.length === 0) return;
    const combined = paintJourney(mapState.mapReference, journey as JourneyJson);
    /* istanbul ignore else -- a non-empty legs array always yields at least the endpoint coords */
    if (combined.length > 0) {
      mapState.mapReference.fitBounds(leaflet.latLngBounds(combined).pad(BOUNDS_PAD));
    }
  });
</script>

<!-- Leaflet host. Initialised once on mount; callers get the instance via `onReady`
     and drive per-tab layers imperatively, so prop-change re-renders never wipe
     the tile layer or pan / zoom state. -->
<div bind:this={domState.mapElement} class="map"></div>

<style>
  .map {
    height: 100%;
    width: 100%;
    min-height: 300px;
  }
</style>
